/**
 * HEAREN BUILD 56 - API Cache Service
 * 
 * Provides multi-level caching for frequently accessed data:
 * - Level 1: Memory (instant)
 * - Level 2: localStorage (persistent, TTL-based)
 * 
 * Features:
 * - TTL-based expiration (configurable per resource type)
 * - In-flight request deduplication (prevents duplicate concurrent requests)
 * - localStorage fallback for offline support
 * - Cache busting on demand
 * - Performance logging with timing metrics
 * - Lang normalization (fr-FR → fr, en-US → en)
 * - iOS soft timeout with retry
 * - V1.1.1: Auto-purge on version change
 */

// V1.1.1: Cache version - increment to force cache purge on deploy
const CACHE_VERSION = 'v1.1.2-build1';

// TTL Constants (in milliseconds)
const TTL = {
  THEMES: 30 * 60 * 1000,      // 30 minutes for themes/channels
  TRACKS: 10 * 60 * 1000,      // 10 minutes for track lists
  USER_DATA: 2 * 60 * 1000,    // 2 minutes for user-specific data
  FAVORITES: 1 * 60 * 1000,    // 1 minute for favorites (near real-time)
  DEFAULT: 10 * 60 * 1000      // 10 minutes default
};

// Export TTL for external use
export const CACHE_TTL = TTL;

// V1.1.1: Check and purge cache on version mismatch
const checkAndPurgeCache = () => {
  try {
    const storedVersion = localStorage.getItem('hearen_cache_version');
    if (storedVersion !== CACHE_VERSION) {
      console.log(`[Cache] Version mismatch: ${storedVersion} → ${CACHE_VERSION}, purging cache...`);
      
      // Purge all cache keys
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('hearen_cache:')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));
      
      // Store new version
      localStorage.setItem('hearen_cache_version', CACHE_VERSION);
      
      console.log(`[Cache] Purged ${keysToRemove.length} cache entries`);
    }
  } catch (e) {
    console.error('[Cache] Error checking cache version:', e);
  }
};

// Run cache purge check on load
checkAndPurgeCache();

// V1.1.1: Async check — if server metadata version differs, wipe stale cache
const checkMetadataOnStartup = async () => {
  try {
    const storedMeta = localStorage.getItem('hearen_metadata_version');
    const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://hearen-backend.onrender.com';
    const resp = await fetch(`${API_BASE.replace(/\/$/, '')}/api/metadata/version`);
    if (!resp.ok) return;
    const data = await resp.json();
    const serverMeta = data.display_names_updated_at;
    if (storedMeta && serverMeta && storedMeta !== serverMeta) {
      console.log(`[Cache] Metadata version changed: ${storedMeta} → ${serverMeta}, purging`);
      invalidateCachePattern('soundscapes:');
      invalidateCachePattern('radio:');
    }
    if (serverMeta) localStorage.setItem('hearen_metadata_version', serverMeta);
  } catch (_) { /* offline or pre-render, skip */ }
};
checkMetadataOnStartup();

// iOS detection
const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || window.Capacitor !== undefined;
};

// Performance timing utility
const perfTimer = () => {
  const start = performance.now();
  return {
    elapsed: () => Math.round(performance.now() - start)
  };
};

// Normalize language code (fr-FR → fr, en-US → en)
export const normalizeLang = (lang) => {
  if (!lang) return 'fr';
  const code = lang.toLowerCase().split('-')[0];
  return ['fr', 'en'].includes(code) ? code : 'fr';
};

// In-memory cache
const memoryCache = new Map();

// In-flight request tracking (for deduplication)
const inFlightRequests = new Map();

/**
 * Cache entry structure
 * @typedef {Object} CacheEntry
 * @property {any} data - Cached data
 * @property {number} timestamp - When the data was cached
 * @property {number} ttl - Time to live in ms
 */

/**
 * Check if a cache entry is still valid
 * @param {CacheEntry} entry 
 * @returns {boolean}
 */
const isValid = (entry) => {
  if (!entry) return false;
  return Date.now() - entry.timestamp < entry.ttl;
};

/**
 * Get data from cache
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null if not found/expired
 */
export const getFromCache = (key) => {
  const timer = perfTimer();
  
  // Try memory cache first
  const memEntry = memoryCache.get(key);
  if (isValid(memEntry)) {
    console.log(`[Perf][Cache] HIT key=${key} source=memory durationMs=${timer.elapsed()}`);
    return memEntry.data;
  }
  
  // Try localStorage fallback
  try {
    const stored = localStorage.getItem(`hearen_cache_${key}`);
    if (stored) {
      const entry = JSON.parse(stored);
      if (isValid(entry)) {
        // Restore to memory cache
        memoryCache.set(key, entry);
        console.log(`[Perf][Cache] HIT key=${key} source=localStorage durationMs=${timer.elapsed()}`);
        return entry.data;
      } else {
        // Clean up expired entry
        localStorage.removeItem(`hearen_cache_${key}`);
      }
    }
  } catch (e) {
    // localStorage not available or parse error
  }
  
  console.log(`[Perf][Cache] MISS key=${key} durationMs=${timer.elapsed()}`);
  return null;
};

/**
 * Set data in cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in ms (default: 10 minutes)
 * @param {boolean} persistToStorage - Whether to persist to localStorage (default: true)
 */
export const setInCache = (key, data, ttl = TTL.DEFAULT, persistToStorage = true) => {
  const entry = {
    data,
    timestamp: Date.now(),
    ttl
  };
  
  // Set in memory
  memoryCache.set(key, entry);
  console.log(`[Perf][Cache] SET key=${key} ttl=${Math.round(ttl / 1000)}s size=${JSON.stringify(data).length}b`);
  
  // Persist to localStorage (for offline support)
  if (persistToStorage) {
    try {
      localStorage.setItem(`hearen_cache_${key}`, JSON.stringify(entry));
    } catch (e) {
      // localStorage full or not available
      console.warn(`[Cache] Could not persist to localStorage: ${key}`);
    }
  }
};

/**
 * Invalidate a cache entry
 * @param {string} key - Cache key
 */
export const invalidateCache = (key) => {
  memoryCache.delete(key);
  try {
    localStorage.removeItem(`hearen_cache_${key}`);
  } catch (e) {}
  console.log(`[Cache] 🗑️ INVALIDATED: ${key}`);
};

/**
 * Invalidate all cache entries matching a pattern
 * @param {string} pattern - Pattern to match (e.g., 'radio_' matches 'radio_themes', 'radio_tracks_nature')
 */
export const invalidateCachePattern = (pattern) => {
  let count = 0;
  
  // Clear memory cache
  for (const key of memoryCache.keys()) {
    if (key.includes(pattern)) {
      memoryCache.delete(key);
      count++;
    }
  }
  
  // Clear localStorage
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('hearen_cache_') && key.includes(pattern)) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {}
  
  console.log(`[Cache] 🗑️ INVALIDATED pattern '${pattern}': ${count} entries`);
};

/**
 * Fetch with caching and in-flight deduplication
 * @param {string} cacheKey - Unique cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @param {Object} options - Options
 * @param {number} options.ttl - Time to live (default: 10 minutes)
 * @param {boolean} options.forceRefresh - Force refresh ignoring cache
 * @param {number} options.timeout - Timeout in ms (iOS default: 8000)
 * @param {number} options.retries - Number of retries on timeout (iOS default: 1)
 * @returns {Promise<any>} - Fetched or cached data
 */
export const cachedFetch = async (cacheKey, fetchFn, options = {}) => {
  const { 
    ttl = TTL.DEFAULT, 
    forceRefresh = false,
    timeout = isIOS() ? 8000 : 15000,
    retries = isIOS() ? 1 : 0
  } = options;
  
  const timer = perfTimer();
  
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getFromCache(cacheKey);
    if (cached !== null) {
      return cached;
    }
  }
  
  // Check if there's already an in-flight request for this key
  if (inFlightRequests.has(cacheKey)) {
    console.log(`[Perf][Cache] DEDUP key=${cacheKey} (waiting for in-flight request)`);
    return inFlightRequests.get(cacheKey);
  }
  
  // Create new request with timeout and retry support
  console.log(`[Perf][Fetch] START key=${cacheKey}`);
  
  const fetchWithTimeout = async (attemptNum = 1) => {
    const attemptTimer = perfTimer();
    
    try {
      // Create timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(`Timeout after ${timeout}ms`)), timeout);
      });
      
      // Race fetch against timeout
      const data = await Promise.race([
        fetchFn(),
        timeoutPromise
      ]);
      
      const size = JSON.stringify(data).length;
      console.log(`[Perf][Fetch] OK key=${cacheKey} status=200 durationMs=${attemptTimer.elapsed()} size=${size}b attempt=${attemptNum}`);
      
      return data;
      
    } catch (error) {
      console.warn(`[Perf][Fetch] ERROR key=${cacheKey} error=${error.message} durationMs=${attemptTimer.elapsed()} attempt=${attemptNum}`);
      
      // Retry if we have retries left and it's a timeout
      if (attemptNum <= retries && error.message.includes('Timeout')) {
        console.log(`[Perf][Fetch] RETRY key=${cacheKey} attempt=${attemptNum + 1}/${retries + 1}`);
        return fetchWithTimeout(attemptNum + 1);
      }
      
      throw error;
    }
  };
  
  const requestPromise = (async () => {
    try {
      const data = await fetchWithTimeout();
      
      // V1.1.2: Never cache empty sound responses (prevents stale empty cache)
      const sounds = data?.sounds;
      const isEmptyTrackResponse = cacheKey.includes('tracks:') && (
        (Array.isArray(sounds) && sounds.length === 0) ||
        (Array.isArray(data) && data.length === 0)
      );
      if (isEmptyTrackResponse) {
        console.warn(`[Cache] SKIP caching empty sounds response for key=${cacheKey}`);
        return data;
      }
      
      // Cache the result
      setInCache(cacheKey, data, ttl);
      
      console.log(`[Perf][Fetch] COMPLETE key=${cacheKey} totalDurationMs=${timer.elapsed()}`);
      return data;
    } finally {
      // Remove from in-flight requests
      inFlightRequests.delete(cacheKey);
    }
  })();
  
  // Track in-flight request
  inFlightRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
};

/**
 * Preload data into cache (fire and forget)
 * @param {string} cacheKey - Cache key
 * @param {Function} fetchFn - Async function to fetch data
 * @param {number} ttl - Time to live
 */
export const preload = (cacheKey, fetchFn, ttl = TTL.DEFAULT) => {
  // Only preload if not already cached
  if (getFromCache(cacheKey) === null && !inFlightRequests.has(cacheKey)) {
    console.log(`[Perf][Cache] PRELOAD key=${cacheKey}`);
    cachedFetch(cacheKey, fetchFn, { ttl }).catch(err => {
      console.warn(`[Perf][Cache] PRELOAD_FAILED key=${cacheKey} error=${err.message}`);
    });
  } else {
    console.log(`[Perf][Cache] PRELOAD_SKIP key=${cacheKey} (already cached or in-flight)`);
  }
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  memoryCache.clear();
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith('hearen_cache_')) {
        localStorage.removeItem(key);
      }
    }
  } catch (e) {}
  console.log('[Cache] 🗑️ ALL CACHE CLEARED');
};

// Cache keys constants - BUILD 56: Include lang for proper cache separation
export const CACHE_KEYS = {
  // Radio
  radioThemes: (lang = 'fr') => `radio:themes:lang=${normalizeLang(lang)}`,
  radioChannels: (lang = 'fr') => `radio:channels:lang=${normalizeLang(lang)}`,
  radioTracks: (channelId, lang = 'fr') => `radio:tracks:channel=${channelId}:lang=${normalizeLang(lang)}`,
  
  // Soundscapes
  soundscapesThemes: (lang = 'fr') => `soundscapes:themes:lang=${normalizeLang(lang)}`,
  soundscapesTracks: (theme, lang = 'fr') => `soundscapes:tracks:theme=${theme}:lang=${normalizeLang(lang)}`,
  
  // User data
  userSettings: (userId) => `user:settings:id=${userId}`,
  personalizedSettings: (userId) => `user:personalized:id=${userId}`,
  userFavorites: (userId) => `user:favorites:id=${userId}`,
  
  // Legacy compatibility (deprecated, use functions above)
  RADIO_THEMES: 'radio:themes:lang=fr',
  RADIO_CHANNELS: 'radio:channels:lang=fr',
  SOUNDSCAPES_THEMES: 'soundscapes:themes:lang=fr',
};

// Last selected theme persistence
const LAST_SELECTED_KEYS = {
  RADIO_THEME: 'hearen_last_radio_theme',
  SOUNDSCAPES_THEME: 'hearen_last_soundscapes_theme'
};

/**
 * Get last selected theme
 * @param {string} type - 'radio' or 'soundscapes'
 * @returns {string|null} - Last selected theme ID or null
 */
export const getLastSelectedTheme = (type) => {
  try {
    const key = type === 'radio' ? LAST_SELECTED_KEYS.RADIO_THEME : LAST_SELECTED_KEYS.SOUNDSCAPES_THEME;
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

/**
 * Save last selected theme
 * @param {string} type - 'radio' or 'soundscapes'
 * @param {string} themeId - Theme ID to save
 */
export const saveLastSelectedTheme = (type, themeId) => {
  try {
    const key = type === 'radio' ? LAST_SELECTED_KEYS.RADIO_THEME : LAST_SELECTED_KEYS.SOUNDSCAPES_THEME;
    localStorage.setItem(key, themeId);
    console.log(`[Perf][LastTheme] SAVED type=${type} theme=${themeId}`);
  } catch (e) {
    // localStorage not available
  }
};

export default {
  getFromCache,
  setInCache,
  invalidateCache,
  invalidateCachePattern,
  cachedFetch,
  preload,
  clearAllCache,
  normalizeLang,
  getLastSelectedTheme,
  saveLastSelectedTheme,
  CACHE_KEYS,
  CACHE_TTL: TTL
};
